// =============================================================================
// features/crm/CrmListPage.tsx — Tela /crm: lista de leads.
//
// DS:
//   - Tabela densa (DS §9.7): th caption-style, hover de linha, avatares com
//     --grad-rondonia, coluna de valor em JetBrains Mono (td-amount).
//   - Responsivo (F27-S04, doc 24 §6): ResponsiveTable (components/ui) degrada
//     a mesma LEAD_COLUMNS para cards empilhados abaixo de `md:` — tabela
//     densa permanece no desktop, sem duplicar lógica de apresentação.
//   - Filtros (F58-S08, CRM-03): busca por nome/telefone, status, período,
//     "Sem agente", "Parados" e — com `leads:triage` — "Sem cidade" (leva à
//     visão Triagem). O estado vive na URL: `/crm?stale=true` do dashboard
//     abre a lista já filtrada. Filtro ativo vira etiqueta removível.
//   - Stats row: 4 KPIs (DS §9.8) de GET /api/leads/stats com os MESMOS
//     filtros da lista — não a página de 20 (CRM-05).
//   - "Novo lead", "Importar" e o CTA do estado vazio só com `leads:write`
//     (PERM-08): a API negaria, a tela não oferece.
//   - Paginação server-side (page/limit).
//   - Loading: skeletons que respeitam layout final.
//   - Empty: SVG inline + CTA "Adicionar primeiro lead".
//
// LGPD:
//   - Telefone: SEMPRE maskPhone() — nunca raw.
//   - Email: SEMPRE truncateEmail() em listagens.
//   - CPF: nunca exibido.
//   - Sem console.log(lead).
//
// Visão "Triagem" (F55-S07): terceiro segmento, SÓ para quem tem
// `leads:triage` (decisão D12: administração e gestão geral). Sem a permissão
// o segmento não existe e `?view=triagem` cai na lista — o servidor também
// recusa (403), a tela só evita oferecer o que não pode ser usado.
// =============================================================================

import type { Appointment, LeadResponse } from '@elemento/shared-schemas';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import type { AvatarVariant } from '../../components/ui/Avatar';
import { Avatar } from '../../components/ui/Avatar';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import type { ResponsiveTableColumn } from '../../components/ui/ResponsiveTable';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { useAssignableAgents } from '../../hooks/admin/useAgents';
import { parseLeadUrlFilters, writeLeadUrlFilters } from '../../hooks/crm/leadFilters';
import type { LeadUrlFilters } from '../../hooks/crm/leadFilters';
import type { LeadFilters } from '../../hooks/crm/types';
import {
  STATUS_META,
  SOURCE_LABEL,
  maskPhone,
  truncateEmail,
  formatDate,
} from '../../hooks/crm/types';
import {
  useAppointmentsGate,
  useDueLeadIds,
  useLeadNextActions,
} from '../../hooks/crm/useLeadAppointments';
import { useLeads } from '../../hooks/crm/useLeads';
import { useLeadStats } from '../../hooks/crm/useLeadStats';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAuth } from '../../lib/auth-store';
import { cn } from '../../lib/cn';
import { KanbanPage } from '../../pages/kanban/KanbanPage';
import { ContextualHelp } from '../help/contextual';
// Contrato de saída do módulo comercial (F33-S06) — mesmo badge da Agenda e
// do Kanban, "próxima ação" nunca diverge entre telas (doc 26 §22).
import { NextActionBadge } from '../sales';

import { ActiveFilterChips } from './components/ActiveFilterChips';
import { CrmFilterBar } from './components/CrmFilterBar';
import type { CrmQuickFilterKey } from './components/CrmQuickFilters';
import { CrmQuickFilters } from './components/CrmQuickFilters';
import { LeadStatCards } from './components/LeadStatCards';
import { TriageQueue } from './components/TriageQueue';
import { activeFilterChips, clearedFilters, removeFilter } from './listFilters';
import { NewLeadModal } from './NewLeadModal';
import { LEAD_TRIAGE_PERMISSION, resolveCrmView } from './triage';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const AVATAR_VARIANTS: AvatarVariant[] = ['rondonia', 'azul', 'verde', 'amarelo'];

function avatarVariantForName(name: string): AvatarVariant {
  // Determinístico: hash simples da primeira letra para variedade
  const code = name.charCodeAt(0) % AVATAR_VARIANTS.length;
  return AVATAR_VARIANTS[code] ?? 'rondonia';
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────
// Skeleton da tabela/cards de leads é responsabilidade do ResponsiveTable
// (mesmo componente, mesmas colunas — sem duplicar por breakpoint).

// ─── Empty state ──────────────────────────────────────────────────────────────

interface EmptyStateProps {
  /** Há filtro ativo? Então o vazio é "nenhum resultado", não "nenhum lead". */
  filtered: boolean;
  onClearFilters: () => void;
  /** Só com `leads:write`; sem a permissão não há CTA de criação. */
  onAdd: (() => void) | null;
}

function EmptyState({ filtered, onClearFilters, onAdd }: EmptyStateProps): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-4">
      {/* Ilustração SVG inline */}
      <svg
        viewBox="0 0 120 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-32 h-auto opacity-40"
        aria-hidden="true"
      >
        {/* Fundo de mesa */}
        <ellipse cx="60" cy="85" rx="50" ry="8" fill="var(--surface-muted)" />
        {/* Pasta / arquivo */}
        <rect
          x="25"
          y="20"
          width="70"
          height="55"
          rx="6"
          fill="var(--bg-elev-2)"
          stroke="var(--border-strong)"
          strokeWidth="1.5"
        />
        <rect x="25" y="20" width="70" height="12" rx="6" fill="var(--surface-muted)" />
        {/* Linhas de conteúdo */}
        <rect x="35" y="42" width="30" height="3" rx="1.5" fill="var(--border-strong)" />
        <rect x="35" y="50" width="50" height="3" rx="1.5" fill="var(--border-strong)" />
        <rect x="35" y="58" width="40" height="3" rx="1.5" fill="var(--border-strong)" />
        {/* Símbolo + */}
        <circle cx="88" cy="28" r="12" fill="var(--brand-verde)" />
        <path d="M88 23v10M83 28h10" stroke="white" strokeWidth="2" strokeLinecap="round" />
      </svg>

      <div className="flex flex-col gap-1">
        <p
          className="font-display font-bold text-ink"
          style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.025em' }}
        >
          Nenhum lead encontrado
        </p>
        <p className="font-sans text-sm text-ink-3 max-w-xs">
          {filtered
            ? 'Nenhum lead combina com os filtros aplicados.'
            : onAdd
              ? 'Ainda não há leads cadastrados. Comece adicionando o primeiro.'
              : 'Ainda não há leads para você ver aqui.'}
        </p>
      </div>

      {filtered ? (
        <Button variant="outline" onClick={onClearFilters}>
          Limpar filtros
        </Button>
      ) : onAdd ? (
        <Button variant="primary" onClick={onAdd}>
          Adicionar primeiro lead
        </Button>
      ) : null}
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────
// Bloco único (não é mais linha de tabela) — substitui tabela/cards nos dois
// modos quando `isError`, igual ao padrão já usado em features/relatorios.

function LeadsErrorState({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <p className="font-sans text-sm text-danger">Erro ao carregar leads.</p>
      <button
        type="button"
        className="font-sans text-xs font-semibold text-azul hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20 rounded-xs"
        onClick={onRetry}
      >
        Tentar novamente
      </button>
    </div>
  );
}

// ─── Colunas (ResponsiveTable — DS §9.7 + doc 24 §6) ─────────────────────────
// A MESMA definição alimenta a tabela (desktop) e os cards empilhados
// (mobile) — sem duplicar a lógica de apresentação por breakpoint.

const BASE_LEAD_COLUMNS: ResponsiveTableColumn<LeadResponse>[] = [
  {
    key: 'lead',
    header: 'Lead',
    primary: true,
    widthClassName: 'w-[260px]',
    cell: (lead) => (
      <Link
        to={`/crm/${lead.id}`}
        className="flex items-center gap-3 hover:text-azul transition-colors group/link"
        title={`Ver detalhes de ${lead.name}`}
      >
        <Avatar
          name={lead.name}
          variant={avatarVariantForName(lead.name)}
          size="md"
          className="shrink-0 transition-transform group-hover/link:scale-105 duration-fast"
        />
        <div className="min-w-0">
          <p
            className="font-sans font-semibold text-sm text-ink truncate group-hover/link:text-azul transition-colors"
            style={{ maxWidth: 180 }}
          >
            {lead.name}
          </p>
          <p className="font-sans text-xs text-ink-4 truncate">
            {lead.city_name ?? 'Sem cidade'}
            {lead.agent_id ? ' · Agente atribuído' : ''}
          </p>
        </div>
      </Link>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    widthClassName: 'w-[130px]',
    cell: (lead) => {
      const statusMeta = STATUS_META[lead.status];
      return (
        <div className="flex flex-col gap-1 items-start">
          <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
          {lead.kanban_stage && (
            <span
              className="font-sans text-[10px] text-ink-4 truncate max-w-[120px]"
              title={`Estágio do Kanban: ${lead.kanban_stage.name}`}
            >
              ◳ {lead.kanban_stage.name}
            </span>
          )}
        </div>
      );
    },
  },
  {
    key: 'phone',
    header: 'Telefone',
    widthClassName: 'w-[150px]',
    // LGPD: SEMPRE maskPhone() — nunca o telefone bruto.
    cell: (lead) => (
      <span
        className="font-mono text-sm text-ink-2"
        style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', letterSpacing: '-0.01em' }}
      >
        {maskPhone(lead.phone_e164)}
      </span>
    ),
  },
  {
    key: 'email',
    header: 'E-mail',
    hideBelow: 'lg',
    // LGPD (mínima exposição): e-mail só no desktop largo, como antes — não
    // ampliar a superfície de exibição para o card mobile (F27-S04 review).
    hideInCard: true,
    // LGPD: SEMPRE truncateEmail() em listagens.
    cell: (lead) =>
      lead.email ? (
        <span className="font-sans text-sm text-ink-3">{truncateEmail(lead.email)}</span>
      ) : (
        <span className="text-ink-4 text-sm">—</span>
      ),
  },
  {
    key: 'source',
    header: 'Canal',
    hideBelow: 'md',
    widthClassName: 'w-[110px]',
    cell: (lead) => (
      <span className="font-sans text-xs text-ink-3">
        {SOURCE_LABEL[lead.source] ?? lead.source}
      </span>
    ),
  },
  {
    key: 'createdAt',
    header: 'Criado',
    hideBelow: 'xl',
    align: 'right',
    widthClassName: 'w-[110px]',
    cell: (lead) => (
      <span className="font-sans text-xs text-ink-4">{formatDate(lead.created_at)}</span>
    ),
  },
];

// Coluna "Próxima ação" (F33-S07, doc 26 §6.2) — equivalente tabular do chip
// do card do Kanban. Inserida condicionalmente: só existe com o gate de
// permissão/flag aberto (buildLeadColumns abaixo), nunca no array base —
// com a flag desligada a tabela fica byte-a-byte igual à de hoje.
function buildNextActionColumn(
  nextActionByLead: Map<string, Appointment>,
  now: Date,
): ResponsiveTableColumn<LeadResponse> {
  return {
    key: 'nextAction',
    header: 'Próxima ação',
    hideBelow: 'lg',
    widthClassName: 'w-[180px]',
    cell: (lead) => {
      const appointment = nextActionByLead.get(lead.id);
      if (!appointment) return <span className="text-ink-4 text-xs">—</span>;
      return <NextActionBadge appointment={appointment} now={now} />;
    },
  };
}

function buildLeadColumns(
  showNextAction: boolean,
  nextActionByLead: Map<string, Appointment>,
  now: Date,
): ResponsiveTableColumn<LeadResponse>[] {
  if (!showNextAction) return BASE_LEAD_COLUMNS;
  // Antes da coluna "Criado" — mantém Lead/Status/Telefone/Email/Canal no topo.
  const insertAt = BASE_LEAD_COLUMNS.findIndex((c) => c.key === 'createdAt');
  const columns = [...BASE_LEAD_COLUMNS];
  columns.splice(
    insertAt === -1 ? columns.length : insertAt,
    0,
    buildNextActionColumn(nextActionByLead, now),
  );
  return columns;
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * CrmListPage — /crm
 * Lista paginada de leads com filtros, stats row e tabela densa.
 */
export function CrmListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, user: currentUser } = useAuth();
  const canTriage = hasPermission(LEAD_TRIAGE_PERMISSION);
  const activeView = resolveCrmView(searchParams.get('view'), canTriage);

  // PERM-08: criar/importar exige `leads:write` (a API negaria com 403).
  const canWrite = hasPermission('leads:write');
  const [modalOpen, setModalOpen] = React.useState(false);

  // ── Filtros da lista na URL (F58-S08, CRM-03) ──────────────────────────────
  // Fonte única: a URL. Mudar filtro = reescrever o query string (replace, sem
  // empilhar histórico) — o link `/crm?stale=true` do dashboard já chega
  // filtrado e o botão Voltar do detalhe preserva o recorte.
  const urlFilters = React.useMemo(() => parseLeadUrlFilters(searchParams), [searchParams]);
  const updateFilters = React.useCallback(
    (update: (current: LeadUrlFilters) => LeadUrlFilters): void => {
      setSearchParams(
        (prev) => {
          const next = writeLeadUrlFilters(prev, update(parseLeadUrlFilters(prev)));
          // "Meus leads" e "Sem agente" se contradizem (a API devolveria 400).
          if (next.get('no_agent') === 'true' && next.get('quick') === 'mine') {
            next.delete('quick');
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const openTriage = React.useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('view', 'triagem');
        next.delete('quick');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ── Filtros rápidos (F33-S07, doc 26 §6.2) ─────────────────────────────────
  // Estado vem direto da URL (mesmo padrão de `activeView` acima) — DoD:
  // "filtros rápidos refletidos na URL e preservados ao voltar do detalhe".
  const quickFilter = (searchParams.get('quick') as CrmQuickFilterKey | null) ?? null;
  const setQuickFilter = React.useCallback(
    (key: CrmQuickFilterKey | null): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (key) next.set('quick', key);
          else next.delete('quick');
          if (key === 'mine') next.delete('no_agent');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const appointmentsGate = useAppointmentsGate();
  const { enabled: campaignsFlagEnabled } = useFeatureFlag('sales.campaigns.enabled');
  const now = React.useMemo(() => new Date(), []);

  // "Meus leads" — resolve o agente do usuário atual por correspondência de
  // nome contra a lista enxuta de agentes atribuíveis (não há, hoje, um
  // endpoint que devolva "meu agente" diretamente — GET /api/agents/assignable
  // é escopado por CIDADE, não por usuário; GET /api/admin/agents tem
  // `userId` mas exige `agents:manage`, que um vendedor comum não tem).
  // Best-effort e não-destrutivo: sem correspondência única, o chip fica
  // desabilitado (CrmQuickFilters.mineAvailable) em vez de filtrar errado.
  const { agents: assignableAgents } = useAssignableAgents();
  // LGPD: nome do PRÓPRIO usuário logado (sessão local), nunca de terceiro.
  const currentUserName = currentUser?.fullName ?? null;
  const myAgentId = React.useMemo(() => {
    if (!currentUserName) return null;
    const matches = assignableAgents.filter((a) => a.display_name === currentUserName);
    return matches.length === 1 ? (matches[0]?.id ?? null) : null;
  }, [assignableAgents, currentUserName]);

  const dueWindow = quickFilter === 'today' || quickFilter === 'overdue' ? quickFilter : null;
  const { leadIds: dueLeadIds, isLoading: dueLoading } = useDueLeadIds(dueWindow ?? 'today', {
    enabled: appointmentsGate.canRead && dueWindow !== null,
  });

  // "Meus leads" reaproveita o filtro `agent_id` já existente e correto
  // (server-side, paginado) — nunca um cálculo local paralelo. "Retorno
  // hoje"/"Atrasados" não têm equivalente server-side (a API de leads não
  // filtra por compromisso), então bump de limite + interseção client-side
  // do lote carregado (mesmo racional de hooks/sales/useAppointmentLeadSummaries.ts
  // "aceitável para o volume do piloto comercial").
  const effectiveFilters: LeadFilters = React.useMemo(() => {
    let next: LeadFilters = { ...urlFilters, limit: 20 };
    if (quickFilter === 'mine' && myAgentId) {
      next = { ...next, agent_id: myAgentId };
    }
    if (dueWindow) {
      next = { ...next, page: 1, limit: 100 };
    }
    return next;
  }, [urlFilters, quickFilter, myAgentId, dueWindow]);

  const { data, isLoading, isError, refetch } = useLeads(effectiveFilters);
  // Cartões: mesmos filtros da lista, sem paginação (toStatsFilters no hook).
  const statsQuery = useLeadStats(effectiveFilters);
  const filterChips = React.useMemo(() => activeFilterChips(urlFilters), [urlFilters]);
  const hasFilters = filterChips.length > 0 || (quickFilter === 'mine' && myAgentId !== null);

  const fetchedLeads = data?.data ?? [];
  const leads = dueWindow ? fetchedLeads.filter((l) => dueLeadIds.has(l.id)) : fetchedLeads;
  const pagination = dueWindow ? undefined : data?.pagination;

  // Coluna "Próxima ação" — só busca compromissos para os leads efetivamente
  // exibidos na página atual (nunca a base inteira).
  const displayedLeadIds = React.useMemo(() => leads.map((l) => l.id), [leads]);
  const { nextActionByLead } = useLeadNextActions(displayedLeadIds, {
    enabled: appointmentsGate.canRead,
  });
  const leadColumns = React.useMemo(
    () => buildLeadColumns(appointmentsGate.canRead, nextActionByLead, now),
    [appointmentsGate.canRead, nextActionByLead, now],
  );

  return (
    <>
      <div
        className="flex flex-col gap-6"
        style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) both' }}
      >
        {/* ── Page header ───────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          {/* Segmented control Lista | Kanban */}
          <div
            className="inline-flex items-center gap-0.5 rounded-md border border-border-subtle p-0.5"
            style={{ background: 'var(--bg-elev-2)' }}
            role="group"
            aria-label="Modo de visualização"
          >
            <button
              type="button"
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('view');
                    return next;
                  },
                  { replace: true },
                )
              }
              aria-pressed={activeView === 'lista'}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px]',
                'font-sans text-sm font-medium',
                'transition-all duration-[200ms] ease-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
                activeView === 'lista'
                  ? 'bg-surface-1 text-ink shadow-e1'
                  : 'text-ink-3 hover:text-ink hover:bg-surface-hover',
              )}
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                className="w-3.5 h-3.5"
                aria-hidden="true"
              >
                <path d="M2 4h12M2 8h12M2 12h12" />
              </svg>
              Lista
            </button>
            <button
              type="button"
              onClick={() =>
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('view', 'kanban');
                    return next;
                  },
                  { replace: true },
                )
              }
              aria-pressed={activeView === 'kanban'}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px]',
                'font-sans text-sm font-medium',
                'transition-all duration-[200ms] ease-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
                activeView === 'kanban'
                  ? 'bg-surface-1 text-ink shadow-e1'
                  : 'text-ink-3 hover:text-ink hover:bg-surface-hover',
              )}
            >
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                className="w-3.5 h-3.5"
                aria-hidden="true"
              >
                <rect x="1" y="2" width="4" height="12" rx="1" />
                <rect x="6" y="2" width="4" height="8" rx="1" />
                <rect x="11" y="2" width="4" height="10" rx="1" />
              </svg>
              Kanban
            </button>
            {canTriage && (
              <button
                type="button"
                onClick={() =>
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('view', 'triagem');
                      next.delete('quick');
                      return next;
                    },
                    { replace: true },
                  )
                }
                aria-pressed={activeView === 'triagem'}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[5px]',
                  'font-sans text-sm font-medium',
                  'transition-all duration-[200ms] ease-out',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
                  activeView === 'triagem'
                    ? 'bg-surface-1 text-ink shadow-e1'
                    : 'text-ink-3 hover:text-ink hover:bg-surface-hover',
                )}
              >
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  className="w-3.5 h-3.5"
                  aria-hidden="true"
                >
                  <path d="M2 3h12l-4.5 5.5V13l-3 1.5V8.5z" />
                </svg>
                Triagem
              </button>
            )}
          </div>

          {/* Ações: Importar + Novo Lead */}
          {canWrite && (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  onClick={() => navigate('/imports/leads/new')}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      className="w-4 h-4"
                      aria-hidden="true"
                    >
                      <path d="M8 2v8" />
                      <path d="M5 7l3 3 3-3" />
                      <path d="M2 12v1a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-1" />
                    </svg>
                  }
                >
                  Importar leads
                </Button>
                {/* ⓘ tutorial de importação — norma 21 §7 */}
                <ContextualHelp featureKey="crm.lead.import" permission="leads:write" />
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="primary"
                  onClick={() => setModalOpen(true)}
                  leftIcon={
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      className="w-4 h-4"
                      aria-hidden="true"
                    >
                      <path d="M8 2v12M2 8h12" />
                    </svg>
                  }
                >
                  Novo lead
                </Button>
                {/* ⓘ tutorial de criação de lead — norma 21 §7 */}
                <ContextualHelp featureKey="crm.lead.create" permission="leads:write" />
              </div>
            </div>
          )}
        </div>

        {/* ── Título da página ──────────────────────────────────────────────── */}
        <div className="-mt-2">
          <div className="flex items-center gap-1">
            <h1
              className="font-display font-bold text-ink"
              style={{
                fontSize: 'var(--text-3xl)',
                letterSpacing: '-0.04em',
                fontVariationSettings: "'opsz' 48",
              }}
            >
              CRM
            </h1>
            {/* ⓘ ajuda contextual — norma 21 §7 */}
            <ContextualHelp featureKey="crm.lead.qualify" className="ml-0.5" />
          </div>
          <p className="font-sans text-sm text-ink-3 mt-1">Gerencie seus leads</p>
        </div>

        {/* ── Filtros rápidos (F33-S07, doc 26 §6.2) ──────────────────────────
            Gate único: com a permissão/flag fechada, esta linha inteira não
            existe no DOM — CRM idêntico ao de hoje (DoD do slot). */}
        {appointmentsGate.canRead && activeView !== 'triagem' && (
          <div style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) 0.02s both' }}>
            <CrmQuickFilters
              active={quickFilter}
              onChange={setQuickFilter}
              mineAvailable={myAgentId !== null}
              showCampaign={campaignsFlagEnabled}
              isLoading={dueLoading}
            />
          </div>
        )}

        {/* ── Conteúdo dependente do modo de visualização ───────────────────── */}
        {activeView === 'triagem' ? (
          <TriageQueue />
        ) : activeView === 'kanban' ? (
          <KanbanPage
            hideHeader
            quickFilterAgentId={quickFilter === 'mine' && myAgentId ? myAgentId : undefined}
            quickFilterLeadIds={dueWindow ? dueLeadIds : undefined}
          />
        ) : (
          <>
            {/* ── Stats row — GET /api/leads/stats, mesmos filtros da lista ─────── */}
            <LeadStatCards
              stats={statsQuery.data}
              isLoading={statsQuery.isLoading}
              isError={statsQuery.isError}
              filtered={hasFilters}
            />

            {/* ── Filtros (URL é a fonte) ───────────────────────────────────────── */}
            <div
              className="flex flex-col gap-3"
              style={{ animation: 'fade-up var(--dur-slow) var(--ease-out) 0.1s both' }}
            >
              <CrmFilterBar
                filters={urlFilters}
                onChange={updateFilters}
                canTriage={canTriage}
                onOpenTriage={openTriage}
              />
              <ActiveFilterChips
                chips={filterChips}
                onRemove={(key) => updateFilters((f) => removeFilter(f, key))}
                onClear={() => updateFilters(clearedFilters)}
              />
            </div>

            {/* ── Tabela (desktop) / Cards (mobile) ────────────────────────────────
                ResponsiveTable (components/ui): mesma definição de colunas
                (leadColumns) alimenta os dois modos — DS §9.7 + doc 24 §6. */}
            <div
              className="rounded-md border border-border overflow-hidden"
              style={{
                background: 'var(--bg-elev-1)',
                boxShadow: 'var(--elev-2)',
                animation: 'fade-up var(--dur-slow) var(--ease-out) 0.15s both',
              }}
            >
              {/* Filtro rápido temporal (Retorno hoje/Atrasados): a API de leads
                  não filtra por compromisso, então a lista é recortada
                  client-side entre os leads mais recentes carregados — nota
                  honesta em vez de fingir paginação exata (doc do slot: "sem
                  cálculo local paralelo" vale para a URGÊNCIA/rótulo, não
                  para a decisão de QUAIS leads mostrar, que não tem
                  equivalente server-side ainda). */}
              {dueWindow && (
                <div
                  className="px-4 py-2 border-b border-border-subtle"
                  style={{ background: 'var(--surface-2)' }}
                >
                  <p className="font-sans text-xs text-ink-3">
                    {dueWindow === 'today' ? 'Retorno hoje' : 'Atrasados'}: {leads.length}{' '}
                    {leads.length === 1 ? 'lead' : 'leads'} entre os mais recentes carregados.
                  </p>
                </div>
              )}

              {isError ? (
                <LeadsErrorState onRetry={refetch} />
              ) : (
                <ResponsiveTable
                  columns={leadColumns}
                  data={leads}
                  getRowKey={(lead) => lead.id}
                  isLoading={isLoading}
                  emptyState={
                    <EmptyState
                      filtered={hasFilters}
                      onClearFilters={() => updateFilters(clearedFilters)}
                      onAdd={canWrite ? () => setModalOpen(true) : null}
                    />
                  }
                  aria-label="Lista de leads"
                />
              )}

              {/* Paginação */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-border-subtle">
                  <p className="font-sans text-xs text-ink-3">
                    {(pagination.page - 1) * pagination.limit + 1}–
                    {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                    {pagination.total} leads
                  </p>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={pagination.page <= 1}
                      onClick={() => updateFilters((f) => ({ ...f, page: f.page - 1 }))}
                      className={cn(
                        'px-3 py-1.5 rounded-sm font-sans text-xs font-medium',
                        'border border-border transition-all duration-fast',
                        'hover:bg-surface-hover hover:border-border-strong',
                        'disabled:opacity-40 disabled:cursor-not-allowed',
                        'focus-visible:ring-2 focus-visible:ring-azul/20',
                      )}
                      aria-label="Página anterior"
                    >
                      ← Anterior
                    </button>
                    <button
                      type="button"
                      disabled={pagination.page >= pagination.totalPages}
                      onClick={() => updateFilters((f) => ({ ...f, page: f.page + 1 }))}
                      className={cn(
                        'px-3 py-1.5 rounded-sm font-sans text-xs font-medium',
                        'border border-border transition-all duration-fast',
                        'hover:bg-surface-hover hover:border-border-strong',
                        'disabled:opacity-40 disabled:cursor-not-allowed',
                        'focus-visible:ring-2 focus-visible:ring-azul/20',
                      )}
                      aria-label="Próxima página"
                    >
                      Próxima →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal de criação — só existe para quem pode criar (leads:write) */}
      {canWrite && <NewLeadModal open={modalOpen} onClose={() => setModalOpen(false)} />}
    </>
  );
}
